import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
    CurrentBrand,
    FacebookPageType,
    InstagramPageType,
    FBPixel,
    InstagramProfileForOnboarding,
    SoundcloudArtistProfile,
    SpotifyArtistSearchResponse,
    TikTokProfileForOnboarding,
    YouTubeChannel,
    AdAccountPageType
} from 'modules/Types';
import { CreateBrandPayload } from 'api/brandApi';
import { track } from 'analytics';
import { PlanType, UserPermissionOnBrand } from 'modules/Const';
import * as Sentry from '@sentry/react'
import useBrand from './useBrand';

export const ONBOARDING_VERSION_V3_GOALS = 'v3_goals'

export enum OnboardingStep {
    ADD_ARTIST = 'add artist',
    PAYWALL = 'paywall',
    GOALS = 'goals',
    CREATE_CAMPAIGN = 'create campaign',
}

interface OnboardingState {
    // States
    isLoadingOnboarding: boolean;

    selectedSpotifyArtist: SpotifyArtistSearchResponse | null;
    selectedInstagramProfile: InstagramProfileForOnboarding | null;
    selectedTikTokProfile: TikTokProfileForOnboarding | null;
    signupWithoutProfileName: string | null;
    selectedGoalUrl: string;

    showGoalsModal: boolean;

    onboardingStep: OnboardingStep;

    // Actions
    setIsLoadingOnboarding: (isLoading: boolean) => void;

    setSelectedSpotifyArtist: (artist: SpotifyArtistSearchResponse | null) => void;
    setSelectedInstagramProfile: (profile: InstagramProfileForOnboarding | null) => void;
    setSelectedTikTokProfile: (profile: TikTokProfileForOnboarding | null) => void;
    setSignupWithoutProfileName: (name: string | null) => void;
    setSelectedGoalUrl: (url: string) => void;

    setShowGoalsModal: (show: boolean) => void;
    setOnboardingStep: (step: OnboardingStep) => void;

    resetOnboarding: () => void;

    createBrandOnboarding: () => Promise<CurrentBrand | undefined>;
}

const useOnboardingV2 = create(immer<OnboardingState>((set, get) => ({
    // States
    isLoadingOnboarding: false,

    selectedSpotifyArtist: null,
    selectedInstagramProfile: null,
    selectedTikTokProfile: null,
    signupWithoutProfileName: null,
    selectedGoalUrl: '',
    onboardingStep: OnboardingStep.ADD_ARTIST,

    showGoalsModal: false,

    // Actions
    setIsLoadingOnboarding: (isLoading: boolean) => set((state) => { state.isLoadingOnboarding = isLoading; }),

    setSelectedSpotifyArtist: (artist: SpotifyArtistSearchResponse | null) => set((state) => { state.selectedSpotifyArtist = artist; }),
    setSelectedInstagramProfile: (profile: InstagramProfileForOnboarding | null) => set((state) => { state.selectedInstagramProfile = profile; }),
    setSelectedTikTokProfile: (profile: TikTokProfileForOnboarding | null) => set((state) => { state.selectedTikTokProfile = profile; }),
    setOnboardingStep: (step: OnboardingStep) => {
        track(`NEW ONBOARDING: STEP ${step}`, {
            version: ONBOARDING_VERSION_V3_GOALS
        })
        set((state) => { state.onboardingStep = step; })
    },
    setShowGoalsModal: (show: boolean) => set((state) => { state.showGoalsModal = show; }),
    setSignupWithoutProfileName: (name: string | null) => set((state) => { state.signupWithoutProfileName = name; }),
    setSelectedGoalUrl: (url: string) => set((state) => { state.selectedGoalUrl = url; }),

    resetOnboarding: () => set((state) => {
        state.selectedSpotifyArtist = null;
        state.selectedInstagramProfile = null;
        state.selectedTikTokProfile = null;
        state.onboardingStep = OnboardingStep.ADD_ARTIST;
        state.signupWithoutProfileName = null;
        state.selectedGoalUrl = '';
    }),

    createBrandOnboarding: async (): Promise<CurrentBrand | undefined> => {
        const { createBrand, setCurrentBrand } = useBrand.getState()
        const {
            selectedSpotifyArtist,
            selectedTikTokProfile,
            selectedInstagramProfile,
            signupWithoutProfileName
        } = get()

        set({ isLoadingOnboarding: true })

        try {
            track('NEW ONBOARDING: CREATING BRAND', {
                version: ONBOARDING_VERSION_V3_GOALS
            })

            const brandName = selectedSpotifyArtist?.name ||
                selectedTikTokProfile?.username ||
                selectedInstagramProfile?.username ||
                signupWithoutProfileName ||
                null

            const customization: CreateBrandPayload['customization'] = {
                // onboarding_answers: onboardingAnswers, // TODO: figure out onboardingAnswers
                spotify_artist: selectedSpotifyArtist || undefined,
                tiktok: selectedTikTokProfile || undefined,
                instagram_profile_for_onboarding: selectedInstagramProfile || undefined
            }

            const payload: CreateBrandPayload = {
                name: brandName,
                customization
            }

            const createdBrand = await createBrand(payload)

            track('Finished Artist Creation', {
                version: ONBOARDING_VERSION_V3_GOALS
            })

            setCurrentBrand(createdBrand)

            // Reset state after successful creation
            set(state => {
                state.selectedSpotifyArtist = null
                state.selectedTikTokProfile = null
                state.selectedInstagramProfile = null
                state.signupWithoutProfileName = null
            })

            return createdBrand
        } catch (e) {
            Sentry.captureException(e)
            console.log('error', e)
            return undefined
        } finally {
            set({ isLoadingOnboarding: false })
        }
    }
}))
);

// NOTE CONNER: currently not used but potentially useful at some point
export const getInitialBrand = (): CurrentBrand => {
    return (
        {
            id: 0,
            name: '',
            image: '',
            slug: '',
            content_initialized: false,
            dashboard_initialized: false,
            tier: PlanType.LITE,
            connections: {
                chartmetric: { id: 0 },
                facebook_ad_account: {} as AdAccountPageType,
                facebook_business_manager: {
                    id: '',
                    name: '',
                    permitted_roles: [],
                    profile_picture_uri: ''
                },
                facebook_page: {} as FacebookPageType,
                facebook_pixel: {} as FBPixel,
                instagram_page: {} as InstagramPageType,
                business_discovery_instagram: {
                    id: '',
                    username: '',
                    profile_picture_url: '',
                    followers_count: 0
                },
                spotify_artist_page: {
                    external_urls: { spotify: '' },
                    followers: { href: null, total: 0 },
                    genres: [],
                    href: null,
                    id: '',
                    images: [],
                    name: '',
                    popularity: 0,
                    type: '',
                    uri: ''
                },
                tiktok: null,
                tiktok_pixel: '',
                google_analytics: '',
                youtube_channel: {} as YouTubeChannel,
                audiomack: {},
                twitter: {
                    url: '',
                    name: '',
                    screen_name: ''
                },
                soundcloud_artist_page: {} as SoundcloudArtistProfile,
                reauthenticateFbUser: false
            },
            website: null,
            permissionLevel: UserPermissionOnBrand.ADMINISTRATOR
        }
    )
}

export default useOnboardingV2;
